import { FileOP, getFileHandler, resolvePath } from "./opfs_common.ts";
import type { OPFSPath, OPFSWorkerMessage, OPFSWorkerResult, OPFSWorkerResultFor } from "./opfs_common.ts";

const opfsWorkerURL = "opfs_worker.js";
const opfsCall = (() => {
    let lastId = 1;
    const callMap: Map<number, (result: OPFSWorkerResult) => unknown> = new Map();
    let workerLoadingPromise: Promise<void> | null = null;
    let opfsWorker: Worker | null = null;
    const loadWorker = async () => {
        const resp = await fetch(opfsWorkerURL);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        opfsWorker = new Worker(url, { type: "module" });
        opfsWorker.addEventListener("message", (evt: MessageEvent<OPFSWorkerResult>) => {
            const result = evt.data;
            const uid = result.uid;
            const resolve = callMap.get(uid);
            if (resolve) resolve(result);
            callMap.delete(uid);
        });
    };
    const ensureWorker = async () => {
        if (opfsWorker === null) {
            if (workerLoadingPromise === null) {
                workerLoadingPromise = loadWorker();
            }
            await workerLoadingPromise;
        }
        return opfsWorker as Worker;
    };
    return async <T extends OPFSWorkerMessage>(msg: T) => {
        const worker = await ensureWorker();
        const pms = new Promise<OPFSWorkerResult>((resolve) => {
            // register callback
            const uid = lastId++;
            msg.uid = uid;
            callMap.set(uid, resolve);
            // send message to worker
            if (msg.op === FileOP.Write) {
                worker.postMessage(msg, [msg.buffer]);
            } else {
                worker.postMessage(msg);
            }
        });
        const result = await pms;
        if (result.op === FileOP.Error) {
            throw result.err;
        }
        return result as OPFSWorkerResultFor<T>;
    };
})();

export class OPFSFile {
    static #finalizationRegistry = new FinalizationRegistry(async (fd: number) => {
        if (fd <= 0) return;
        await opfsCall({ op: FileOP.Close, fd });
    });
    #hd: FileSystemFileHandle;
    #fd: number;
    constructor(fileHandler: FileSystemFileHandle) {
        this.#hd = fileHandler;
        this.#fd = 0;
    }

    static fromPath = async (pathParts: OPFSPath, createDir: boolean = false, createFile: boolean = false) => {
        const fileHandler = await getFileHandler(pathParts, createDir, createFile);
        return new OPFSFile(fileHandler);
    };

    getSize = async () => {
        const file = await this.#hd.getFile();
        return file.size;
    };

    open = async () => {
        if (this.#fd > 0) return;
        const result = await opfsCall({
            op: FileOP.Open,
            path: await resolvePath(this.#hd),
        });
        if (this.#fd > 0) {
            // maybe another promise already opened the file.
            await opfsCall({
                op: FileOP.Close,
                fd: result.fd,
            });
        } else {
            this.#fd = result.fd;
            // make sure file would be closed.
            OPFSFile.#finalizationRegistry.register(this, result.fd, this);
        }
    };

    close = async () => {
        if (this.#fd <= 0) return;
        await opfsCall({ op: FileOP.Close, fd: this.#fd });
        this.#fd = 0;
        OPFSFile.#finalizationRegistry.unregister(this);
    };

    flush = async () => {
        if (this.#fd <= 0) return;
        await opfsCall({ op: FileOP.Flush, fd: this.#fd });
        this.#fd = 0;
    };

    write = async (data: AllowSharedBufferSource, offset: number = 0) => {
        if (this.#fd <= 0) return 0;
        const result = await opfsCall({ op: FileOP.Write, fd: this.#fd, buffer: data, at: offset });
        return result.count;
    };

    truncate = async (newSize: number) => {
        if (this.#fd <= 0) return;
        await opfsCall({ op: FileOP.Truncate, fd: this.#fd, newSize });
    };

    read = async (count: number = -1, offset: number = 0) => {
        if (this.#fd <= 0) new Uint8Array();
        if (count < 0) {
            count = await this.getSize();
        }
        const result = await opfsCall({ op: FileOP.Read, fd: this.#fd, count, at: offset });
        return new Uint8Array(result.buffer);
    };

    getHandler = () => {
        return this.#hd;
    };
}
